import { useState, useEffect, useCallback } from "react";
import {
  Settings, LayoutDashboard, Eye, EyeOff, GripVertical,
  ChevronDown, ChevronRight, Save, RefreshCw, Columns3,
  PanelTop, Sidebar, BarChart3, GitBranch, Phone, ClipboardCheck,
  Compass, Building2, Truck, Calendar, Mail, DollarSign,
  MessageSquare, Briefcase, GraduationCap, FileText, ToggleLeft, ToggleRight,
  PanelBottom
} from "lucide-react";
import WidgetConfigPanel from "./WidgetConfigPanel";
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl();
const authFetch = (url, opts = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, { ...opts, credentials: "include", headers: { ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
};

const DASHBOARD_ICONS = {
  telepro: Phone, r1: ClipboardCheck, r2: Compass, amo: Building2,
  chantiers: Truck, calendrier: Calendar, communications_ia: Mail,
  marketing: MessageSquare, administratif: FileText, grands_comptes: Briefcase,
  compta_finances: DollarSign, formations: GraduationCap,
  comite_direction: Settings, tests: Settings,
};

// ── Default configurable sections per dashboard ──
const DEFAULT_SECTIONS = {
  header: {
    label: "Header", icon: PanelTop,
    items: [
      { id: "btn_mon_espace", label: "Bouton Mon Espace", enabled: true },
      { id: "btn_calendrier", label: "Bouton Calendrier", enabled: true },
      { id: "btn_add_lead", label: "Bouton Ajouter Lead", enabled: true },
      { id: "btn_add_devis", label: "Bouton Ajouter Devis", enabled: false },
      { id: "btn_notifications", label: "Notifications", enabled: true },
      { id: "btn_profil", label: "Profil compact", enabled: true },
      { id: "btn_refresh", label: "Bouton Actualiser", enabled: true },
      { id: "widget_sania_banner", label: "Banniere Performance SAN-IA", enabled: true },
    ],
  },
  kpis: {
    label: "KPIs / Widgets haut", icon: BarChart3,
    items: [
      { id: "kpi_rdv_today", label: "RDV aujourd'hui", enabled: true },
      { id: "kpi_leads_new", label: "Nouveaux leads", enabled: true },
      { id: "kpi_calls_today", label: "Appels du jour", enabled: true },
      { id: "kpi_conversion", label: "Taux conversion", enabled: false },
      { id: "kpi_objectif", label: "Objectif mensuel", enabled: false },
      { id: "kpi_ca_mois", label: "CA du mois", enabled: false },
    ],
  },
  sidebar: {
    label: "Sidebar gauche", icon: Sidebar,
    items: [
      { id: "sb_mes_rappels", label: "Mes Rappels", enabled: true },
      { id: "sb_visa_ia", label: "Visa IA", enabled: true },
      { id: "sb_communication_interne", label: "Communication Interne", enabled: true },
      { id: "sb_ma_todo_list", label: "Ma Todo List", enabled: true },
      { id: "sb_filtre_telepros", label: "Filtre Telepros", enabled: true },
      { id: "sb_stats_ringover", label: "Stats Ringover", enabled: true },
    ],
  },
  columns: {
    label: "Colonnes tableau", icon: Columns3,
    items: [
      { id: "col_nom", label: "Nom / Prenom", enabled: true },
      { id: "col_telephone", label: "Telephone", enabled: true },
      { id: "col_email", label: "Email", enabled: false },
      { id: "col_statut", label: "Statut", enabled: true },
      { id: "col_source", label: "Source", enabled: true },
      { id: "col_date_creation", label: "Date creation", enabled: true },
      { id: "col_assignee", label: "Assignee", enabled: false },
      { id: "col_date_rdv", label: "Date RDV", enabled: false },
      { id: "col_ville", label: "Ville", enabled: false },
      { id: "col_montant", label: "Montant devis", enabled: false },
    ],
  },
  pipeline: {
    label: "Etapes pipeline", icon: GitBranch,
    items: [
      { id: "pipe_new", label: "Nouveau", enabled: true },
      { id: "pipe_contacted", label: "Contacte", enabled: true },
      { id: "pipe_rdv_planned", label: "RDV planifie", enabled: true },
      { id: "pipe_rdv_done", label: "RDV effectue", enabled: true },
      { id: "pipe_devis_sent", label: "Devis envoye", enabled: false },
      { id: "pipe_signed", label: "Signe", enabled: false },
      { id: "pipe_lost", label: "Perdu", enabled: false },
    ],
  },
  banners_footer: {
    label: "Bannieres footer", icon: PanelBottom,
    items: [
      { id: "bf_leads_live", label: "Banniere Nouveaux Leads", enabled: true },
      { id: "bf_nouveau_r1", label: "Banniere Nouveau R1", enabled: true },
    ],
  },
};

// ── Section toggle list ──
function SectionBlock({ sectionKey, section, config, onChange }) {
  const [open, setOpen] = useState(false);
  const Icon = section.icon;
  const items = Array.isArray(config?.[sectionKey]) ? config[sectionKey] : section.items;
  const enabledCount = items.filter(i => i.enabled).length;

  const toggleItem = (itemId) => {
    const updated = items.map(i => i.id === itemId ? { ...i, enabled: !i.enabled } : i);
    onChange(sectionKey, updated);
  };

  const moveItem = (idx, dir) => {
    const arr = [...items];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    onChange(sectionKey, arr);
  };

  return (
    <div className="border border-slate-700/50 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-800/40 transition-colors"
        data-testid={`section-${sectionKey}`}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium">{section.label}</span>
          <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">
            {enabledCount}/{items.length}
          </span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
      </button>

      {open && (
        <div className="border-t border-slate-700/50 divide-y divide-slate-800/50">
          {items.map((item, idx) => (
            <div key={item.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-800/20" data-testid={`item-${item.id}`}>
              <div className="flex flex-col gap-0.5">
                <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="text-slate-600 hover:text-slate-400 disabled:opacity-20">
                  <ChevronRight className="w-3 h-3 -rotate-90" />
                </button>
                <button onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1} className="text-slate-600 hover:text-slate-400 disabled:opacity-20">
                  <ChevronRight className="w-3 h-3 rotate-90" />
                </button>
              </div>
              <GripVertical className="w-3.5 h-3.5 text-slate-600" />
              <span className="flex-1 text-sm text-slate-300">{item.label}</span>
              <button
                onClick={() => toggleItem(item.id)}
                className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                  item.enabled
                    ? "bg-emerald-500/20 text-emerald-400"
                    : "bg-slate-700/50 text-slate-500"
                }`}
              >
                {item.enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Panel ──
export default function DashboardConfigPanel() {
  const [dashboards, setDashboards] = useState([]);
  const [selectedDash, setSelectedDash] = useState(null);
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [configurableDashboards, setConfigurableDashboards] = useState({ tests: true });

  // Load configurable dashboards state
  const loadConfigurableState = useCallback(async () => {
    try {
      const resp = await authFetch(`${API}/api/config/configurable-dashboards`);
      if (resp.ok) {
        const data = await resp.json();
        setConfigurableDashboards(data.dashboards || { tests: true });
      }
    } catch (e) { /* default to tests only */ }
  }, []);

  const toggleConfigurable = async (dashId) => {
    const updated = { ...configurableDashboards, [dashId]: !configurableDashboards[dashId] };
    setConfigurableDashboards(updated);
    try {
      await authFetch(`${API}/api/config/configurable-dashboards`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dashboards: updated }),
      });
    } catch (e) { console.error("Toggle configurable error:", e); }
  };

  // Load dashboard list from backend
  const loadDashboards = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/api/config/dashboards-config`);
      let backendDashboards = {};
      if (resp.ok) {
        const data = await resp.json();
        backendDashboards = data.dashboards || data;
      }

      // All known dashboards — always show all, merge with backend data
      const ALL_DASHBOARDS = {
        telepro: { label: "Telepro", order: 1 },
        r1: { label: "R1", order: 2 },
        r2: { label: "R2", order: 3 },
        amo: { label: "AMO", order: 4 },
        chantiers: { label: "Chantiers", order: 5 },
        calendrier: { label: "Calendrier", order: 6 },
        communications_ia: { label: "Com. IA", order: 7 },
        marketing: { label: "Marketing", order: 8 },
        administratif: { label: "Admin", order: 9 },
        grands_comptes: { label: "Grands Comptes", order: 10 },
        compta_finances: { label: "Compta", order: 11 },
        formations: { label: "Formations", order: 12 },
        comite_direction: { label: "Comite Direction", order: 13 },
        tests: { label: "Tests", order: 14 },
      };

      const list = Object.entries(ALL_DASHBOARDS).map(([id, def]) => ({
        id,
        label: backendDashboards[id]?.label || def.label,
        order: backendDashboards[id]?.order || def.order,
      })).sort((a, b) => a.order - b.order);

      setDashboards(list);
      if (list.length > 0 && !selectedDash) setSelectedDash(list[0].id);
    } catch (e) { console.error("Load dashboards error:", e); }
    setLoading(false);
  }, [selectedDash]);

  // Load saved config for a specific dashboard
  const loadDashConfig = useCallback(async (dashId) => {
    try {
      const resp = await authFetch(`${API}/api/config/dashboard/${dashId}`);
      if (resp.ok) {
        const data = await resp.json();
        if (data && Object.keys(data).length > 0) {
          setConfigs(prev => ({ ...prev, [dashId]: data }));
        }
      }
    } catch (e) { /* ignore - will use defaults */ }
  }, []);

  useEffect(() => { loadDashboards(); loadConfigurableState(); }, [loadDashboards, loadConfigurableState]);
  useEffect(() => { if (selectedDash) loadDashConfig(selectedDash); }, [selectedDash, loadDashConfig]);

  const currentConfig = configs[selectedDash] || {};

  const handleSectionChange = (sectionKey, items) => {
    setConfigs(prev => ({
      ...prev,
      [selectedDash]: {
        ...(prev[selectedDash] || {}),
        [sectionKey]: items,
      },
    }));
  };

  const handleSave = async () => {
    if (!selectedDash) return;
    setSaving(true);
    try {
      const resp = await authFetch(`${API}/api/config/dashboard/${selectedDash}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(configs[selectedDash] || {}),
      });
      if (resp.ok) {
        setSaveMsg("Configuration sauvegardee");
      } else {
        setSaveMsg("Erreur de sauvegarde");
      }
    } catch (e) { setSaveMsg("Erreur: " + e.message); }
    setSaving(false);
    setTimeout(() => setSaveMsg(null), 3000);
  };

  const DashIcon = selectedDash ? (DASHBOARD_ICONS[selectedDash] || LayoutDashboard) : LayoutDashboard;

  return (
    <div data-testid="dashboard-config-panel" className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
              <Settings className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold" data-testid="config-panel-title">Configuration Dashboards</h2>
              <p className="text-sm text-slate-400">Configurez le header, les KPIs, la sidebar, les colonnes et le pipeline de chaque dashboard</p>
            </div>
          </div>
          <button
            data-testid="config-save-btn"
            onClick={handleSave}
            disabled={saving || !selectedDash}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Sauvegarder
          </button>
        </div>
        {saveMsg && (
          <div className={`mt-3 text-sm font-medium ${saveMsg.includes("Erreur") ? "text-red-400" : "text-emerald-400"}`}>
            {saveMsg}
          </div>
        )}
      </div>

      {/* Dashboard Selector */}
      <div className="flex flex-wrap gap-1.5 bg-slate-800/50 rounded-xl p-1.5" data-testid="dashboard-selector">
        {loading ? (
          <div className="px-4 py-2 text-sm text-slate-500">Chargement...</div>
        ) : (
          dashboards.map(d => {
            const Icon = DASHBOARD_ICONS[d.id] || LayoutDashboard;
            const isConfigurable = !!configurableDashboards[d.id];
            return (
              <button
                key={d.id}
                data-testid={`select-dash-${d.id}`}
                onClick={() => setSelectedDash(d.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  selectedDash === d.id
                    ? "bg-blue-600 text-white"
                    : "text-slate-400 hover:text-white hover:bg-slate-700/50"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {d.label.replace(/^[^\s]+\s/, "")}
                {isConfigurable && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />}
              </button>
            );
          })
        )}
      </div>

      {/* Selected Dashboard Config */}
      {selectedDash && (
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-3">
              <DashIcon className="w-5 h-5 text-blue-400" />
              <h3 className="text-base font-semibold">
                {dashboards.find(d => d.id === selectedDash)?.label || selectedDash}
              </h3>
            </div>
            <button
              data-testid={`toggle-configurable-${selectedDash}`}
              onClick={() => toggleConfigurable(selectedDash)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                configurableDashboards[selectedDash]
                  ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25"
                  : "bg-slate-700/50 text-slate-400 border border-slate-600/30 hover:bg-slate-700 hover:text-slate-300"
              }`}
            >
              {configurableDashboards[selectedDash]
                ? <><ToggleRight className="w-4 h-4" /> Mode configurable actif</>
                : <><ToggleLeft className="w-4 h-4" /> Mode classique</>
              }
            </button>
          </div>

          {!configurableDashboards[selectedDash] && (
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 text-center">
              <p className="text-sm text-slate-400">Ce dashboard utilise le <span className="text-slate-300 font-medium">mode classique</span> (code en dur).</p>
              <p className="text-xs text-slate-500 mt-1">Activez le mode configurable pour piloter son header, sidebar, widgets et colonnes depuis cette interface.</p>
            </div>
          )}

          {configurableDashboards[selectedDash] && Object.entries(DEFAULT_SECTIONS).map(([key, section]) => (
            <SectionBlock
              key={key}
              sectionKey={key}
              section={section}
              config={currentConfig}
              onChange={handleSectionChange}
            />
          ))}
        </div>
      )}

      {!selectedDash && !loading && (
        <div className="text-center py-12 text-slate-500">
          <LayoutDashboard className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Selectionnez un dashboard pour le configurer</p>
        </div>
      )}

      {/* Section Configuration Widgets — below dashboard config */}
      <div className="mt-8 border-t border-slate-700/50 pt-6">
        <WidgetConfigPanel />
      </div>
    </div>
  );
}
